import {
  IBookableDatesInfo,
  IBookInfo,
  IKakaoPayBookInfo,
  IDateInfo,
  IBookResultInfo,
  ITempBookResult,
} from "@/domain/entities/book";
import { ISeatInfo } from "@/domain/entities/book";
import {
  BookHistoryDateCriteria,
  BookIntervalCode,
  SERVICE_UID,
} from "@/types";
import { combine, Result } from "neverthrow";
import { HttpError } from "../shared/errors/httpError";
import { ParseError } from "../shared/errors/parseError";
import { IHttpService } from "../shared/httpService";
import {
  HttpBookableDatesInfoResponse,
  IBookRepository,
  HttpSeatsInfoResponse,
  HttpBookInfoListResponse,
  HttpBookInfoResponse,
  IBookableDatesInfoParser,
  ISeatsInfoParser,
  IBookRequestParser,
  IBookInfoParser,
  IKakaoPayBookResponse,
  IKakaoPayBookParser,
  HttpBookResultInfoResponse,
  IBookResultInfoParser,
  ICancelKakaoPayRequest,
  HttpBookTempResult,
  ITempBookResultParser,
} from "./bookRepository.types";
import useGetAccessToken from "@/use/useGetAccessToken";
import useGetProtocolHost from "@/use/useGetProtocolHost";
import { inject, injectable } from "inversify";
import TYPES from "@/ioc/identifiers";
import qs from "qs";
import { IBaseParser, IBaseResponse } from "../shared/baseParser";
import { IResultData } from "@/domain/entities/shared/result";
import dayjs from "dayjs";

@injectable()
export class BookRepository implements IBookRepository {
  @inject(TYPES.IBookableDatesInfoParser)
  private bookableDatesInfoParser!: IBookableDatesInfoParser;

  @inject(TYPES.ISeatsInfoParser)
  private seatsInfoParser!: ISeatsInfoParser;

  @inject(TYPES.IBookRequestParser)
  private bookRequestParser!: IBookRequestParser;

  @inject(TYPES.IBookInfoParser)
  private bookInfoParser!: IBookInfoParser;

  @inject(TYPES.IKakaoPayBookParser)
  private kakaoPayBookParser!: IKakaoPayBookParser;

  @inject(TYPES.IBaseParser)
  private baseParser!: IBaseParser;

  @inject(TYPES.IBookResultInfoParser)
  private bookResultInfoParser!: IBookResultInfoParser;

  @inject(TYPES.ITempBookResultParser)
  private bookTempResultParser!: ITempBookResultParser;

  private httpService: IHttpService;

  constructor(@inject(TYPES.IHttpService) httpService: IHttpService) {
    this.httpService = httpService;
    this.httpService.initService(
      `${useGetProtocolHost()}/aibos/client/api/${
        process.env.VUE_APP_API_VERSION
      }/service/${SERVICE_UID}/book`
    );
  }

  async getBookableDatesInfos(
    turnId: string,
    getOnBusStopId: string,
    getOffBusStopId: string,
    fetchLatestOnly = false,
    bookIntervalCode?: BookIntervalCode,
    targetSeatNo?: string
  ): Promise<Result<IBookableDatesInfo[], ParseError | HttpError>> {
    const parseTo = (response: HttpBookableDatesInfoResponse) => {
      const bookableDatesInfo = response.data.map(
        this.bookableDatesInfoParser.toDomain
      );
      return combine(bookableDatesInfo);
    };
    return await this.httpService.get<
      HttpBookableDatesInfoResponse,
      IBookableDatesInfo[]
    >(
      {
        url: "/bookable-date",
        config: {
          headers: {
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
          params: {
            lineTurnUid: turnId,
            onStationUid: getOnBusStopId,
            offStationUid: getOffBusStopId,
            limit: fetchLatestOnly ? 1 : null,
            bookIntervalCode,
            withCalendarYn: !fetchLatestOnly,
            targetSeatNo,
          },
        },
      },
      { parseTo }
    );
  }

  async getSeatsInfos(
    turnId: string,
    date: string,
    isMonthlyBook: boolean,
    allocUid?: string
  ): Promise<Result<ISeatInfo[][][], ParseError | HttpError>> {
    const parseTo = (response: HttpSeatsInfoResponse) => {
      return this.seatsInfoParser.toDomain(response.data.seats ?? []);
    };

    const params = {
      lineTurnUid: turnId,
      date: isMonthlyBook ? undefined : date,
      month: isMonthlyBook ? dayjs(date).format("YYYY-MM") : undefined,
      allocUid: isMonthlyBook ? undefined : allocUid,
    };

    return await this.httpService.get<HttpSeatsInfoResponse, ISeatInfo[][][]>(
      {
        url: "/bookable-seat",
        config: {
          headers: {
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
          params,
        },
      },
      { parseTo }
    );
  }

  async getBookInfos(
    bookStatusCodesStr: string,
    sort: string,
    fromDateCriteria?: BookHistoryDateCriteria,
    fromDate?: string,
    toDateCriteria?: BookHistoryDateCriteria,
    toDate?: string
  ): Promise<Result<IBookInfo[], ParseError | HttpError>> {
    const parseTo = (response: HttpBookInfoListResponse) => {
      const bookInfoList = response.data.map(this.bookInfoParser.toDomain);
      return combine(bookInfoList);
    };
    let url = `?bookStatusCodes=${bookStatusCodesStr}&sortOrder=${sort}`;
    url += fromDate ? `&${fromDateCriteria}DtmFrom=${fromDate}` : "";
    url += toDate ? `&${toDateCriteria}DtmTo=${toDate}` : "";
    return await this.httpService.get<HttpBookInfoListResponse, IBookInfo[]>(
      {
        url,
        config: {
          headers: {
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
        },
      },
      { parseTo }
    );
  }

  async getBookResultInfo(
    bookUid: string
  ): Promise<Result<IBookResultInfo, ParseError | HttpError>> {
    const parseTo = (response: HttpBookResultInfoResponse) => {
      const bookResultInfo = this.bookResultInfoParser.toDomain(response.data);
      return bookResultInfo;
    };
    return await this.httpService.get<
      HttpBookResultInfoResponse,
      IBookResultInfo
    >(
      {
        url: `/${bookUid}/detail`,
        config: {
          headers: {
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
        },
      },
      { parseTo }
    );
  }

  async getBookInfoDetail(
    bookId: string,
    bookDetailId: string
  ): Promise<Result<IBookInfo, ParseError | HttpError>> {
    const parseTo = (response: HttpBookInfoResponse) => {
      const bookInfo = this.bookInfoParser.toDomain(response.data);
      return bookInfo;
    };
    return await this.httpService.get<HttpBookInfoResponse, IBookInfo>(
      {
        url: `/${bookId}?bookDetailUid=${bookDetailId}`,
        config: {
          headers: {
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
        },
      },
      { parseTo }
    );
  }

  async requestKakaoPay(
    autoSeatAssgin: boolean,
    seatsSelectable: boolean,
    turnId: string,
    getOnStationId: string,
    getOffStationId: string,
    dateInfos: IDateInfo[],
    isMonthlyBook: boolean
  ): Promise<Result<IKakaoPayBookInfo, ParseError | HttpError>> {
    const parseTo = (response: IKakaoPayBookResponse) => {
      const bookInfo = this.kakaoPayBookParser.toDomain(response.data);
      return bookInfo;
    };

    const data = this.bookRequestParser.fromDomain(
      autoSeatAssgin,
      seatsSelectable,
      turnId,
      getOnStationId,
      getOffStationId,
      dateInfos,
      isMonthlyBook
    );
    return await this.httpService.post<
      IKakaoPayBookResponse,
      IKakaoPayBookInfo
    >(
      {
        url: "/book-kakaopay",
        data,
        config: {
          baseURL: `${useGetProtocolHost()}/aibos/client/api/${
            process.env.VUE_APP_API_VERSION
          }/service/${SERVICE_UID}`,
          headers: {
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
        },
      },
      { parseTo }
    );
  }

  async failKakaoPay(
    payUid: string,
    bookUid: string
  ): Promise<Result<IResultData, ParseError | HttpError>> {
    const parseTo = (response: IBaseResponse) => {
      return this.baseParser.toDomain(response);
    };
    const data = {
      payUid,
      bookUid,
    };
    return await this.httpService.delete<IBaseResponse, IResultData>(
      {
        url: "/fail-kakaopay",
        config: {
          baseURL: `${useGetProtocolHost()}/aibos/client/api/${
            process.env.VUE_APP_API_VERSION
          }/service/${SERVICE_UID}`,
          headers: {
            "content-type": "application/x-www-form-urlencoded",
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
          data: qs.stringify(data),
        },
      },
      { parseTo }
    );
  }

  async approveKakaoPay(
    payUid: string,
    pgToken: string
  ): Promise<Result<IResultData, ParseError | HttpError>> {
    const parseTo = (response: IBaseResponse) => {
      return this.baseParser.toDomain(response);
    };
    const data = {
      payUid,
      pgToken,
    };
    return await this.httpService.put<IBaseResponse, IResultData>(
      {
        url: "/approve-kakaopay",
        data: qs.stringify(data),
        config: {
          baseURL: `${useGetProtocolHost()}/aibos/client/api/${
            process.env.VUE_APP_API_VERSION
          }/service/${SERVICE_UID}`,
          headers: {
            "content-type": "application/x-www-form-urlencoded",
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
        },
      },
      { parseTo }
    );
  }

  async cancelKakaoPay(
    bookUid: string,
    bookDetailUid?: string
  ): Promise<Result<IResultData, ParseError | HttpError>> {
    const parseTo = (response: IBaseResponse) => {
      return this.baseParser.toDomain(response);
    };
    const data: ICancelKakaoPayRequest = {
      bookUid,
    };
    if (bookDetailUid) {
      data.bookDetailUid = bookDetailUid;
    }
    return await this.httpService.delete<IBaseResponse, IResultData>(
      {
        url: "/cancel-kakaopay",
        config: {
          baseURL: `${useGetProtocolHost()}/aibos/client/api/${
            process.env.VUE_APP_API_VERSION
          }/service/${SERVICE_UID}`,
          headers: {
            "content-type": "application/x-www-form-urlencoded",
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
          data: qs.stringify(data),
        },
      },
      { parseTo }
    );
  }

  async failKcp(
    payUid: string,
    bookUid: string
  ): Promise<Result<IResultData, ParseError | HttpError>> {
    const parseTo = (response: IBaseResponse) => {
      return this.baseParser.toDomain(response);
    };
    const data = {
      payUid,
      bookUid,
      accessToken: useGetAccessToken(),
    };
    return await this.httpService.delete<IBaseResponse, IResultData>(
      {
        url: "/fail-kcp",
        config: {
          baseURL: `${useGetProtocolHost()}/aibos/client/api/${
            process.env.VUE_APP_API_VERSION
          }/service/${SERVICE_UID}`,
          headers: {
            "content-type": "application/x-www-form-urlencoded",
          },
          data: qs.stringify(data),
        },
      },
      { parseTo }
    );
  }

  async requestTempBook(
    autoSeatAssgin: boolean,
    seatsSelectable: boolean,
    turnId: string,
    getOnStationId: string,
    getOffStationId: string,
    dateInfos: IDateInfo[],
    isMonthlyBook = false
  ): Promise<Result<ITempBookResult, ParseError | HttpError>> {
    const parseTo = (response: HttpBookTempResult) => {
      return this.bookTempResultParser.toDomain(response);
    };
    const data = this.bookRequestParser.fromDomain(
      autoSeatAssgin,
      seatsSelectable,
      turnId,
      getOnStationId,
      getOffStationId,
      dateInfos,
      isMonthlyBook
    );
    return await this.httpService.post<HttpBookTempResult, ITempBookResult>(
      {
        url: "/book-temp",
        data: data,
        config: {
          baseURL: `${useGetProtocolHost()}/aibos/client/api/${
            process.env.VUE_APP_API_VERSION
          }/service/${SERVICE_UID}`,
          headers: {
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
        },
      },
      { parseTo }
    );
  }

  async requestApproveBook(
    bookUid: string
  ): Promise<Result<IResultData, ParseError | HttpError>> {
    const parseTo = (response: IBaseResponse) => {
      return this.baseParser.toDomain(response);
    };
    const data = {
      bookUid,
    };
    return await this.httpService.put<IBaseResponse, IResultData>(
      {
        url: "/approve-free-temp",
        data: qs.stringify(data),
        config: {
          baseURL: `${useGetProtocolHost()}/aibos/client/api/${
            process.env.VUE_APP_API_VERSION
          }/service/${SERVICE_UID}`,
          headers: {
            "content-type": "application/x-www-form-urlencoded",
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
        },
      },
      { parseTo }
    );
  }

  async requestCancelBook(
    bookId: string,
    bookDetailId?: string
  ): Promise<Result<IResultData, ParseError | HttpError>> {
    const parseTo = (response: IBaseResponse) => {
      return this.baseParser.toDomain(response);
    };
    const data = {
      bookUid: bookId,
      bookDetailUid: bookDetailId,
    };
    return await this.httpService.delete<IBaseResponse, IResultData>(
      {
        url: "/cancel-free",
        config: {
          baseURL: `${useGetProtocolHost()}/aibos/client/api/${
            process.env.VUE_APP_API_VERSION
          }/service/${SERVICE_UID}`,
          headers: {
            "content-type": "application/x-www-form-urlencoded",
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
          data: qs.stringify(data),
        },
      },
      { parseTo }
    );
  }

  async requestFailBook(
    bookId: string
  ): Promise<Result<IResultData, ParseError | HttpError>> {
    const parseTo = (response: IBaseResponse) => {
      return this.baseParser.toDomain(response);
    };
    const data = {
      bookUid: bookId,
    };
    return await this.httpService.delete<IBaseResponse, IResultData>(
      {
        url: "/fail-book-temp",
        config: {
          baseURL: `${useGetProtocolHost()}/aibos/client/api/${
            process.env.VUE_APP_API_VERSION
          }/service/${SERVICE_UID}`,
          headers: {
            "content-type": "application/x-www-form-urlencoded",
            Authorization: `Bearer ${useGetAccessToken()}`,
          },
          data: qs.stringify(data),
        },
      },
      { parseTo }
    );
  }
}
